/* Keep both top-level menus available while the category list scrolls. */
class LaserlabMenuTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.tabs = [...this.querySelectorAll('[data-menu-tab]')];
    this.panels = [...this.querySelectorAll('[data-menu-panel]')];
    this.drawer = this.closest('menu-drawer');
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectMenu(tab.dataset.menuTab, true));
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else return;
        event.preventDefault();
        this.selectMenu(this.tabs[next].dataset.menuTab, true);
      });
    });
    this.selectMenu(this.dataset.initialMenu);
  }

  selectMenu(name, restoreFocus = false) {
    const selected = this.tabs.find(tab => tab.dataset.menuTab === name);
    if (!selected) return;
    if (this.drawer?.hasAttribute('data-search-active')) {
      this.querySelector('[data-menu-search-form]')?.exitMenuSearch?.(false);
    }
    this.panels.forEach(panel => {
      const active = panel.dataset.menuPanel === name;
      if (!active) {
        // Reset deeper categories immediately, cancelling native slide timers.
        panel.querySelectorAll('details[is="menu-drawer-details"]').forEach(details => {
          details.cancelPendingAnimation?.();
          details.parent?.classList.remove('active');
          details.classList.remove('active');
          details.removeAttribute('open');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        });
      }
      panel.hidden = !active;
    });
    this.tabs.forEach(tab => {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    const scroller = this.querySelector('.menu-drawer__menus');
    if (scroller) scroller.scrollTop = 0;
    if (restoreFocus) {
      // Refresh the native focus trap after replacing the visible links or search.
      if (this.drawer?.open && window.FoxTheme?.a11y) FoxTheme.a11y.trapFocus(this.drawer, selected);
      else selected.focus({ preventScroll: true });
    }
  }
}
customElements.define('laserlab-menu-tabs', LaserlabMenuTabs);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
  const dropdown = event.target.closest('.ll-nav-dropdown[open]');
  if (dropdown) dropdown.querySelector('summary')?.focus();
}, true);

window.addEventListener('pageshow', event => {
  if (!event.persisted) return;
  document.querySelectorAll('.ll-nav-dropdown[open]').forEach(dropdown => {
    dropdown.open = false;
  });
  document.getElementById('MenuDrawer')?.resetAfterHistoryNavigation?.();
});

/* Release the layout that disappears when crossing the mobile breakpoint. */
(() => {
  const desktop = window.matchMedia('(min-width: 1024px)');
  const visibleControl = selector => [...document.querySelectorAll(selector)]
    .find(control => control.getClientRects().length && getComputedStyle(control).visibility !== 'hidden');

  desktop.addEventListener('change', () => {
    if (!desktop.matches) {
      const dropdowns = [...document.querySelectorAll('.ll-nav-dropdown[open]')];
      const restoreFocus = dropdowns.some(dropdown => dropdown.contains(document.activeElement));
      dropdowns.forEach(dropdown => { dropdown.open = false; });
      if (restoreFocus) visibleControl('.header .menu-drawer-button')?.focus({ preventScroll: true });
      return;
    }

    const menu = document.getElementById('MenuDrawer');
    if (!menu?.open || typeof menu.hide !== 'function') return;
    // Let native hide cleanup restore focus to a visible desktop control.
    menu.activeElement = visibleControl('.header .ll-nav-dropdown > summary, .header .header__logo a');
    menu.hide();
  });
})();

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const closeButton = event.target.closest('.ll-nav-panel__close');
  if (!closeButton) return;
  const dropdown = closeButton.closest('.ll-nav-dropdown');
  if (!dropdown) return;
  dropdown.open = false;
  dropdown.querySelector('summary')?.focus();
});

/* Local category changes must release the menu's focus trap before focusing results. */
const closeLaserlabCategoryMenu = (link, heading, isCurrent) => {
  const focusDestination = () => {
    if (!heading?.isConnected || !isCurrent()) return;
    if (!heading.hasAttribute('tabindex')) {
      heading.setAttribute('tabindex', '-1');
      heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
    }
    heading.focus({ preventScroll: true });
  };
  const drawer = link.closest('menu-drawer');
  if (drawer?.open && typeof drawer.hide === 'function') {
    // The native hide promise includes its transition. Its deferred focus cleanup
    // must finish before moving focus from the menu opener to the Shop heading.
    Promise.resolve(drawer.hide()).then(() => setTimeout(focusDestination));
    return;
  }
  const dropdown = link.closest('.ll-nav-dropdown');
  if (dropdown?.open) {
    const afterClose = () => {
      if (dropdown.hasAttribute('open')) return;
      dropdown.removeEventListener('toggle', afterClose);
      dropdown.querySelector('summary')?.removeEventListener('click', cancelFocus);
      focusDestination();
    };
    const cancelFocus = () => dropdown.removeEventListener('toggle', afterClose);
    dropdown.addEventListener('toggle', afterClose);
    dropdown.querySelector('summary')?.addEventListener('click', cancelFocus, { once: true });
    dropdown.open = false;
  }
};

document.addEventListener('laserlab:category-selected', event => {
  if (!(event.target instanceof Element) || !(event.detail?.heading instanceof HTMLElement)) return;
  const expectedUrl = location.href;
  closeLaserlabCategoryMenu(event.target, event.detail.heading, () => location.href === expectedUrl);
});

/* Hash-based Shop filters keep the page in place, so close their menu explicitly. */
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(event.target instanceof Element)) return;
  const link = event.target.closest('.ll-nav-dropdown a[href], #MenuDrawer a[href]');
  if (!link || link.hasAttribute('download') || (link.target && link.target.toLowerCase() !== '_self')) return;

  const destination = new URL(link.href, location.href);
  const current = new URL(location.href);
  const comparableSearch = url => {
    const params = new URLSearchParams(url.search);
    params.delete('preview_theme_id');
    params.sort();
    return params.toString();
  };
  if (!destination.hash || destination.origin !== current.origin || destination.pathname !== current.pathname || comparableSearch(destination) !== comparableSearch(current)) return;
  const heading = document.querySelector('laserlab-shop [data-shop-heading]');
  if (!heading) return;
  if (destination.search !== current.search) {
    // Preserve the current theme preview while changing only the Shop category.
    event.preventDefault();
    destination.search = current.search;
    if (destination.href !== current.href) {
      history.pushState(null, '', destination);
      window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: current.href, newURL: destination.href }));
    }
  }
  closeLaserlabCategoryMenu(link, heading, () => location.href === destination.href);
  // Outside preview, the browser owns the URL, history and hashchange event.
});
